import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import ColumnComponent from "../../ColumnComponent/ColumnComponent";
import { RootState } from "../../../../store";
import { useDispatch, useSelector } from "react-redux";
import OrderBox from "../../OrderBoxV2/OrderBoxV2";
import PerfectScrollbar from "react-perfect-scrollbar";
import orderReceivedSound from "../../../../assets/sounds/jingle.wav";
import { orderActions } from "../../../../store/order-slice";
import { rfzoTherapyActions } from "../../../../store/rfzoTherapy-slice";
import styles from "./ReceivedCoulmn.module.scss";
import {
  combineAndSortReceivableItems,
  calculateOldestOrderTime,
} from "../../../../utils/receivableItemUtils";
import {
  ReceivableItem,
  isCnCOrder,
  isRFZORequest,
} from "../../../../types/receivable";

type Props = {
  isAdmin?: boolean;
};

const ReceivedColumn = ({ isAdmin }: Props) => {
  const orderState = useSelector((state: RootState) => state.order);
  const rfzoTherapy = useSelector((state: RootState) => state.rfzoTherapy);
  const newOrderDesktopNotification = useSelector(
    (state: RootState) => state.order.newOrderNotification
  );
  const newRFZORequestNotification = useSelector(
    (state: RootState) => state.rfzoTherapy.newRFZORequestNotification
  );
  const dispatch = useDispatch();

  // Remove old separate state management
  // const [orders, setOrders] = useState<any>(<div>Waiting...</div>);
  // const [rfzoRequest, setRfzoRequest] = useState<any>("")

  // Audio and notification state
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout>();
  const audioRef = useRef<HTMLAudioElement>();

  // Countdown timer state - currently for countdown logic only, not displayed
  const [oldestOrderTime, setOldestOrderTime] = useState<number | null>(null);
  const [minutes, setMinutes] = useState<string>("00");
  const [seconds, setSeconds] = useState<string>("00");
  const [countDownAlertColor, setCountDownAlertColor] = useState(false);

  // Suppress unused variable warnings - these are used for countdown timer logic
  // If countdown display is needed later, remove these lines
  void minutes;
  void seconds;
  void countDownAlertColor;

  // Combine and sort all receivable items using our utility
  const sortedReceivableItems = useMemo((): ReceivableItem[] => {
    return combineAndSortReceivableItems(
      orderState.receivedOrders,
      rfzoTherapy.rfzoTherapyRequests
    );
  }, [orderState.receivedOrders, rfzoTherapy.rfzoTherapyRequests]);

  // Calculate total count for display
  const totalReceivableCount = sortedReceivableItems.length;

  // Render sorted items
  const renderReceivableItems = useMemo(() => {
    // Only handle rendering sorted items - no loading logic here
    return sortedReceivableItems.map((item) => {
      const key = `${item.itemType}-${item.id}`;

      if (isCnCOrder(item)) {
        return <OrderBox key={key} order={item} isAdmin={isAdmin} />;
      } else if (isRFZORequest(item)) {
        return <OrderBox key={key} rfzoRequest={item} isAdmin={isAdmin} />;
      }

      return null;
    });
  }, [sortedReceivableItems, isAdmin]); // Clean dependencies

  // Handle loading states separately
  const getDisplayContent = () => {
    const hasNoData = sortedReceivableItems.length === 0;
    const isLoading =
      orderState.loadingOrders || rfzoTherapy.loadingRFZOTherapies;

    // Only show "Waiting..." if we have no data AND something is loading (initial load)
    if (hasNoData && isLoading) {
      return <div>Waiting...</div>;
    }

    // Show "No items" if we have no data and nothing is loading
    if (hasNoData && !isLoading) {
      return <div>Nema stavki</div>;
    }

    // Always show existing data (even during background polling)
    return renderReceivableItems;
  };

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(orderReceivedSound);
      audioRef.current.volume = 0.1;
      audioRef.current.loop = false;
    }

    if (isPlaying && audioRef.current) {
      audioRef.current.play();
      intervalRef.current = setInterval(() => {
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play();
        }
      }, 30000);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      clearInterval(intervalRef.current);
    }

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [isPlaying]);

  useEffect(() => {
    if (orderState.receivedOrders.length > 0) {
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  }, [orderState, setIsPlaying]);
  // }, [orderState.receivedOrders]);    // ===== COUNTDOWN TIMER LOGIC =====
  // Update countdown timer to use the utility function
  useEffect(() => {
    const oldestTime = calculateOldestOrderTime(orderState.receivedOrders);
    setOldestOrderTime(oldestTime);
  }, [orderState.receivedOrders]);

  useEffect(() => {
    if (oldestOrderTime === null) {
      setMinutes("00");
      setSeconds("00");
    }
    const intervalId = setInterval(() => {
      if (oldestOrderTime) {
        const remainingTime = Math.floor(
          600 - (Date.now() / 1000 - oldestOrderTime / 1000)
        );
        if (remainingTime > 0 || remainingTime === null) {
          setCountDownAlertColor(false);
        } else {
          setCountDownAlertColor(true);
        }
        setMinutes(Math.abs(Math.floor(remainingTime / 60)).toString());
        setSeconds(
          Math.abs(remainingTime % 60) < 10
            ? `0${Math.abs(remainingTime % 60)}`
            : Math.abs(remainingTime % 60).toString()
        );
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [oldestOrderTime]); // ===== DESKTOP NOTIFICATIONS =====
  useEffect(() => {
    setTimeout(() => {
      if (Notification.permission !== "granted") {
        Notification.requestPermission();
      }
    }, 3000);
  }, []);

  const onNotificationClick = useCallback(() => {
    window.focus();
    window.location.href = "https://cnc.apoteka.rs";
  }, []);

  const showNewOrderNotification = useCallback(() => {
    // @ts-ignore
    const notification = new Notification("Stigla je nova rezervacija", {
      // body: "Stigla je nova rezervacija",
      icon: "https://apoteka.rs/favicon.svg",
      requireInteraction: true,
    });
    notification.onclick = (e) => onNotificationClick();
  }, [onNotificationClick]);

  const showNewRfzoNotification = useCallback(() => {
    // @ts-ignore
    const notification = new Notification("Stigla je nova RFZO rezervacija", {
      // body: "Stigla je nova RFZO rezervacija",
      icon: "https://apoteka.rs/favicon.svg",
      requireInteraction: true,
    });
    notification.onclick = (e) => onNotificationClick();
  }, [onNotificationClick]);

  useEffect(() => {
    if (newOrderDesktopNotification) {
      if (Notification.permission === "granted") {
        showNewOrderNotification();
      } else {
        Notification.requestPermission().then((permission) => {
          if (permission === "granted") showNewOrderNotification();
        });
      }
      dispatch(orderActions.newOrderDesktopNotification(false));
    }
  }, [newOrderDesktopNotification, dispatch, showNewOrderNotification]);

  useEffect(() => {
    if (newRFZORequestNotification) {
      if (Notification.permission === "granted") {
        showNewRfzoNotification();
      } else {
        Notification.requestPermission().then((permission) => {
          if (permission === "granted") showNewRfzoNotification();
        });
      }
      dispatch(rfzoTherapyActions.newRFZORequestDesktopNotification(false));
    }
  }, [newRFZORequestNotification, dispatch, showNewRfzoNotification]);

  const rightSideContent = orderState.receivedOrders.length > 0 && (
    <span className={styles.RightSideHeader}></span>
  );

  return (
    <ColumnComponent
      leftSideLabel="Pristigle"
      leftSideNumber={totalReceivableCount.toString()}
      rightSide={rightSideContent}
      style={{ flex: "4" }}
    >
      <PerfectScrollbar style={{ padding: "4px" }}>
        {getDisplayContent()}
      </PerfectScrollbar>
    </ColumnComponent>
  );
};

export default ReceivedColumn;
